import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError, type CatalogItem, type CurrentUser, type RequestDetail } from '../api';
import { Alert, Badge, Button, Card, EmptyState, Spinner } from '../components/ui';
import { IconArrowRight, IconCheckCircle, IconClipboard } from '../components/icons';
import { draftFromDetail, draftToPatch, ensureCompulsory, type Draft } from './draft';
import { StepEmployee } from './StepEmployee';
import { StepCatalog } from './StepCatalog';
import { StepFileshare } from './StepFileshare';
import { StepNotes } from './StepNotes';
import { StepReview } from './StepReview';
import { StepExit } from './StepExit';
import { EnrolmentPicker } from './EnrolmentPicker';
import { StepAccounts } from './StepAccounts';
import { StepRevoke } from './StepRevoke';
import { StepReturn } from './StepReturn';
import { StepHandover } from './StepHandover';
import { SignatureDialog } from '../components/SignatureDialog';

/**
 * Two flows, one shell. Steps are matched by key rather than index so adding or
 * reordering one cannot silently shift what a position renders, which is what
 * an index-based chain does the moment the two flows differ in length.
 */
type StepKey =
  | 'employee'
  | 'hardware'
  | 'systems'
  | 'remote'
  | 'sharepoint'
  | 'fileshare'
  | 'mailboxes'
  | 'printers'
  | 'notes'
  | 'review'
  | 'exit'
  | 'accounts'
  | 'revoke'
  | 'return'
  | 'handover';

const ENROLMENT_STEPS: { key: StepKey; label: string }[] = [
  { key: 'employee', label: 'Employee' },
  { key: 'hardware', label: 'Hardware' },
  { key: 'systems', label: 'Systems' },
  { key: 'remote', label: 'Remote access' },
  { key: 'sharepoint', label: 'SharePoint' },
  { key: 'fileshare', label: 'Fileshare' },
  { key: 'mailboxes', label: 'Shared mailboxes' },
  { key: 'printers', label: 'Printer access' },
  { key: 'notes', label: 'Notes' },
  { key: 'review', label: 'Review' },
];

const OFFBOARDING_STEPS: { key: StepKey; label: string }[] = [
  { key: 'exit', label: 'Exit details' },
  { key: 'accounts', label: 'Accounts' },
  { key: 'revoke', label: 'Access to revoke' },
  { key: 'return', label: 'Hardware return' },
  { key: 'handover', label: 'Data handover' },
  { key: 'notes', label: 'Notes' },
  { key: 'review', label: 'Review' },
];

const AUTOSAVE_DELAY_MS = 900;

export function Wizard({ user }: { user: CurrentUser }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef<Draft | null>(null);

  const loaded = useQuery({
    queryKey: ['request', id],
    queryFn: () => api.getRequest(id!),
    enabled: Boolean(id),
  });

  const catalog = useQuery({ queryKey: ['catalog'], queryFn: () => api.catalog() });

  const save = useMutation({
    mutationFn: (next: Draft) => api.patchRequest(id!, draftToPatch(next)),
    onMutate: () => setSaveState('saving'),
    onSuccess: ({ request }) => {
      setSaveState('saved');
      setServerDetail(request);
    },
    onError: () => setSaveState('error'),
  });

  const [serverDetail, setServerDetail] = useState<RequestDetail | null>(null);
  const [signing, setSigning] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: (signature: string) => api.submitRequest(id!, signature),
    onSuccess: () => {
      setSigning(false);
      navigate(`/requests/${id}`);
    },
    onError: (err) => setSubmitError(err instanceof ApiError ? err.message : 'Could not submit the request.'),
  });

  // Load once; from then on the wizard owns the working copy so typing is never
  // interrupted by a refetch landing.
  useEffect(() => {
    if (loaded.data && draft === null) {
      setDraft(draftFromDetail(loaded.data.request));
      setServerDetail(loaded.data.request);
    }
  }, [loaded.data, draft]);

  // The server already holds the compulsory items; this keeps the working
  // copy honest about them before the first save round-trips. No save is
  // scheduled: there is nothing the server does not have.
  useEffect(() => {
    if (!catalog.data || loaded.data?.request.type !== 'ENROLMENT') return;
    setDraft((current) => (current ? ensureCompulsory(current, catalog.data.items) : current));
  }, [catalog.data, loaded.data]);

  const scheduleSave = useCallback(
    (next: Draft) => {
      latest.current = next;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        if (latest.current) save.mutate(latest.current);
      }, AUTOSAVE_DELAY_MS);
    },
    [save],
  );

  const update = useCallback(
    (next: Draft) => {
      setDraft(next);
      setSaveState('idle');
      scheduleSave(next);
    },
    [scheduleSave],
  );

  /** Flush before moving, so a step change never loses the last keystroke. */
  const flush = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    if (latest.current) {
      await save.mutateAsync(latest.current).catch(() => undefined);
      latest.current = null;
    }
  }, [save]);

  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  const stepsRef = useRef<{ key: StepKey; label: string }[]>(ENROLMENT_STEPS);

  async function go(next: number) {
    await flush();
    setStep(Math.max(0, Math.min(stepsRef.current.length - 1, next)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  if (loaded.isPending || catalog.isPending) return <Spinner label="Opening the request" />;

  if (loaded.isError) {
    const notFound = loaded.error instanceof ApiError && loaded.error.status === 404;
    return (
      <EmptyState
        icon={<IconClipboard />}
        tone={notFound ? 'neutral' : 'danger'}
        title={notFound ? 'That request is not here' : 'Could not open the request'}
        body={
          notFound
            ? 'It may have been cancelled, or it belongs to someone else. Drafts are private to the person who raised them.'
            : 'Something went wrong loading it. Try again, and tell IT if it keeps happening.'
        }
        action={<Button onClick={() => navigate('/requests')}>Back to my requests</Button>}
      />
    );
  }

  if (!draft || !loaded.data || !catalog.data) return <Spinner />;

  if (!loaded.data.canEdit) {
    return (
      <EmptyState
        icon={<IconClipboard />}
        title="This one is out of your hands now"
        body={`It is ${loaded.data.request.status.toLowerCase().replace(/_/g, ' ')}, so it can no longer be edited.`}
        action={<Button onClick={() => navigate(`/requests/${id}`)}>View the request</Button>}
      />
    );
  }

  const items = catalog.data.items;
  const byCategory = (category: CatalogItem['category']) => items.filter((item) => item.category === category);
  const detail = serverDetail ?? loaded.data.request;
  const offboarding = detail.type === 'OFFBOARDING';
  const steps = offboarding ? OFFBOARDING_STEPS : ENROLMENT_STEPS;
  stepsRef.current = steps;
  const at = steps[Math.min(step, steps.length - 1)]?.key;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
              {offboarding ? 'Offboarding' : 'New enrolment'}
            </h1>
            <Badge tone="neutral">{detail.reference}</Badge>
          </div>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Everything saves as you go. You can leave and come back to it.
          </p>
        </div>
        <SaveIndicator state={saveState} />
      </div>

      <Stepper
        steps={steps}
        current={step}
        incomplete={incompleteSteps(detail.problems, offboarding)}
        onSelect={(index) => void go(index)}
      />

      <Card className="mt-6 p-6 sm:p-8">
        {at === 'employee' && <StepEmployee draft={draft} onChange={update} problems={detail.problems} />}

        {at === 'hardware' && (
          <StepCatalog
            title="Hardware"
            description="What equipment does this person need? Add the brand, model or serial number where you know it; IT can fill in the rest."
            entries={byCategory('HARDWARE')}
            draft={draft}
            onChange={update}
            showAssetDetails
          />
        )}

        {at === 'systems' && (
          <StepCatalog
            title="Software & system access"
            description="Accounts and systems to set up."
            entries={byCategory('SYSTEM')}
            draft={draft}
            onChange={update}
          />
        )}

        {at === 'remote' && (
          <StepCatalog
            title="Remote access"
            description="Access from outside the office. Add a note where there is something IT should know."
            entries={byCategory('REMOTE_ACCESS')}
            draft={draft}
            onChange={update}
            showNotes
          />
        )}

        {at === 'sharepoint' && (
          <StepCatalog
            title="SharePoint sites"
            description="Which SharePoint sites they need."
            entries={byCategory('SHAREPOINT_SITE')}
            draft={draft}
            onChange={update}
          />
        )}

        {at === 'fileshare' && <StepFileshare entries={byCategory('FILESHARE_FOLDER')} draft={draft} onChange={update} />}

        {at === 'mailboxes' && (
          <StepCatalog
            title="Shared mailboxes"
            description="Which shared mailboxes they need access to."
            entries={byCategory('SHARED_MAILBOX')}
            draft={draft}
            onChange={update}
            showNotes
          />
        )}

        {at === 'printers' && (
          <StepCatalog
            title="Printer access"
            description="Which printers they need to be able to use."
            entries={byCategory('PRINTER')}
            draft={draft}
            onChange={update}
          />
        )}

        {at === 'exit' && (
          <>
            <EnrolmentPicker
              request={detail}
              onLinked={(next) => {
                setServerDetail(next);
                setDraft(draftFromDetail(next));
              }}
            />
            <StepExit draft={draft} onChange={update} problems={detail.problems} />
          </>
        )}

        {at === 'accounts' && <StepAccounts draft={draft} onChange={update} problems={detail.problems} />}

        {at === 'revoke' && (
          <StepRevoke
            draft={draft}
            onChange={update}
            groups={[
              { key: 'SYSTEM', label: 'Software & systems', entries: byCategory('SYSTEM') },
              { key: 'REMOTE_ACCESS', label: 'Remote access', entries: byCategory('REMOTE_ACCESS') },
              { key: 'SHAREPOINT_SITE', label: 'SharePoint sites', entries: byCategory('SHAREPOINT_SITE') },
              { key: 'FILESHARE_FOLDER', label: 'Fileshare folders', entries: byCategory('FILESHARE_FOLDER') },
              { key: 'SHARED_MAILBOX', label: 'Shared mailboxes', entries: byCategory('SHARED_MAILBOX') },
              { key: 'PRINTER', label: 'Printer access', entries: byCategory('PRINTER') },
            ]}
          />
        )}

        {at === 'return' && <StepReturn entries={byCategory('HARDWARE')} draft={draft} onChange={update} />}

        {at === 'handover' && <StepHandover draft={draft} onChange={update} />}

        {at === 'notes' && <StepNotes draft={draft} onChange={update} />}

        {at === 'review' && (
          <StepReview
            draft={draft}
            detail={detail}
            onJumpToStep={(index) => void go(index)}
            saving={save.isPending}
            onSubmit={() => {
              setSubmitError(null);
              void flush().then(() => setSigning(true));
            }}
          />
        )}
      </Card>

      <SignatureDialog
        open={signing}
        title="Sign and submit"
        attestation={`You are submitting ${detail.reference} for ${
          draft.employee.fullName || 'this employee'
        }. Your signature goes onto the signed record along with the time, your address and a hash of the request exactly as it stands now. After this it goes to the approvers and you can no longer edit it.`}
        confirmLabel="Sign and submit"
        signerName={user.displayName}
        busy={submit.isPending}
        error={submitError}
        onCancel={() => setSigning(false)}
        onConfirm={(dataUri) => submit.mutate(dataUri)}
      />

      <div className="mt-6 flex items-center justify-between gap-3">
        <Button variant="secondary" onClick={() => void go(step - 1)} disabled={step === 0}>
          Back
        </Button>

        <span className="text-xs text-slate-500 dark:text-slate-400">
          Step {step + 1} of {steps.length}
        </span>

        {step < steps.length - 1 ? (
          <Button onClick={() => void go(step + 1)}>
            Next
            <IconArrowRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button variant="secondary" onClick={() => void flush().then(() => navigate('/requests'))}>
            Save and close
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Which step a readiness problem sends somebody back to. A problem that
 * belongs to no single step (nothing requested at all) marks none.
 */
const ENROLMENT_PROBLEM_STEP: Record<string, StepKey> = {
  fullName: 'employee',
  departmentId: 'employee',
  startDate: 'employee',
  employmentType: 'employee',
  contractEndDate: 'employee',
  takingOverExistingAccount: 'employee',
  takeoverAccountDetails: 'employee',
  locationType: 'employee',
  locationOther: 'employee',
};

const OFFBOARDING_PROBLEM_STEP: Record<string, StepKey> = {
  fullName: 'exit',
  departmentId: 'exit',
  lastWorkingDay: 'exit',
  exitReason: 'exit',
  exitReasonOther: 'exit',
  mailboxAction: 'accounts',
  forwardUntil: 'accounts',
};

function incompleteSteps(problems: { field: string }[], offboarding: boolean): Set<StepKey> {
  const map = offboarding ? OFFBOARDING_PROBLEM_STEP : ENROLMENT_PROBLEM_STEP;
  const keys = new Set<StepKey>();
  for (const problem of problems) {
    const key = map[problem.field];
    if (key) keys.add(key);
  }
  return keys;
}

function Stepper({
  steps,
  current,
  incomplete,
  onSelect,
}: {
  steps: { key: StepKey; label: string }[];
  current: number;
  incomplete: Set<StepKey>;
  onSelect: (index: number) => void;
}) {
  return (
    <ol className="flex flex-wrap gap-x-1 gap-y-2">
      {steps.map((step, index) => {
        // A tick means complete, not merely visited: a step somebody jumped
        // past with a required field still empty keeps its number.
        const done = index < current && !incomplete.has(step.key);
        const active = index === current;
        return (
          <li key={step.key}>
            <button
              type="button"
              onClick={() => onSelect(index)}
              aria-current={active ? 'step' : undefined}
              aria-label={`Step ${index + 1}: ${step.label}${done ? ', complete' : ''}`}
              className={
                active
                  ? 'flex items-center gap-2 rounded-lg bg-brand-800 px-3 py-1.5 text-xs font-medium text-white dark:bg-brand-600'
                  : done
                    ? 'flex items-center gap-2 rounded-lg bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-800 dark:bg-brand-900/50 dark:text-brand-100'
                    : 'flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs text-slate-500 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'
              }
            >
              {done ? (
                <IconCheckCircle className="h-3.5 w-3.5" />
              ) : (
                <span className="tabular-nums opacity-60">{index + 1}</span>
              )}
              {step.label}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function SaveIndicator({ state }: { state: 'idle' | 'saving' | 'saved' | 'error' }) {
  if (state === 'error') return <Badge tone="danger" dot>Not saved: check your connection</Badge>;
  if (state === 'saving') return <Badge tone="neutral" dot>Saving…</Badge>;
  if (state === 'saved') return <Badge tone="success" dot>Saved</Badge>;
  return <span className="text-xs text-slate-500 dark:text-slate-400">All changes save automatically</span>;
}
